import { api } from './api';

/**
 * Passkey do operador (change platform-backoffice-hardening, C14, tarefas 14.4 a 14.6).
 *
 * A passkey é **só step-up**: confirma uma ação sensível no lugar do código TOTP e nunca entra no
 * login. O RP é o host exato do backoffice; a API confere a origem, o desafio (de uso único, preso
 * a conta, sessão, propósito e alvo) e a verificação do usuário.
 *
 * As opções vêm da API em JSON (campos binários em base64url) e viram `ArrayBuffer` aqui para o
 * `navigator.credentials`; a credencial volta em JSON, no mesmo formato.
 */

export const PASSKEYS_URL = '/platform/auth/passkeys';
export const PASSKEY_REGISTRATION_OPTIONS_URL = '/platform/auth/passkeys/registration/options';
export const PASSKEY_REGISTRATION_COMPLETE_URL = '/platform/auth/passkeys/registration/complete';
export const PASSKEY_STEP_UP_OPTIONS_URL = '/platform/auth/step-up/passkey/options';

/** Rótulos aceitos pela API (lista fechada), com o texto da tela. */
export const PASSKEY_LABELS = [
  { value: 'notebook', text: 'Notebook' },
  { value: 'celular', text: 'Celular' },
  { value: 'chave_fisica', text: 'Chave física' },
  { value: 'outro', text: 'Outro' },
] as const;

export type PasskeyLabel = (typeof PASSKEY_LABELS)[number]['value'];

export function passkeyLabelText(value: string): string {
  return PASSKEY_LABELS.find((l) => l.value === value)?.text ?? value;
}

/**
 * Os propósitos do step-up, iguais ao catálogo da API (`PlatformStepUpPurposes`): o desafio de
 * passkey é pedido para um propósito e um alvo, e só vale na ação com os mesmos dois.
 */
export const STEP_UP_PURPOSES = {
  tenantDeactivate: { purpose: 'tenant.deactivate', targetType: 'tenant' },
  subscriptionCancel: { purpose: 'subscription.cancel', targetType: 'tenant' },
  pricingApply: { purpose: 'pricing.apply_to_subscriptions', targetType: 'plan' },
  aiUnitPurchaseRefund: { purpose: 'ai_units.purchase_refund', targetType: 'ai_unit_purchase' },
  agentAuthorizationApprove: { purpose: 'agent_authorization.approve', targetType: 'agent_authorization_request' },
  // Consentimento pessoal do operador (change platform-mcp-operator-access): mesmo alvo, outro propósito.
  operatorAuthorizationApprove: {
    purpose: 'operator_authorization.approve',
    targetType: 'agent_authorization_request',
  },
  // Segredo do conector web do claude.ai (change platform-mcp-operator-access, OA-D12): a própria conta.
  operatorWebConnectorSecretIssue: {
    purpose: 'operator_web_connector_secret.issue',
    targetType: 'platform_account',
  },
  // Ordens de serviço do agente de escrita (change platform-mcp-writes, 1.17).
  agentWorkOrderCreate: { purpose: 'agent_work_order.create', targetType: 'tenant' },
  agentWorkOrderCreateBatch: { purpose: 'agent_work_order.create_batch', targetType: 'agent_client' },
  // platform-mcp-writes (1.18): "Preparar autorização", preso ao cliente de agente.
  agentAuthorizationIntentPrepare: { purpose: 'agent_authorization_intent.prepare', targetType: 'agent_client' },
  // platform-mcp-writes (1.18): o teto do perfil com escrita ("Teto ampliado com espera").
  agentClientCeilingWrite: { purpose: 'agent_client.ceiling_write', targetType: 'agent_client' },
} as const;

/**
 * Os propósitos da proposta de agente (change platform-mcp-writes, 1.19), iguais a
 * `PlatformStepUpPurposes.AgentProposalApprove` e `AgentProposalRevert`: o alvo é o id da proposta.
 */
export const STEP_UP_PURPOSES_AGENT_PROPOSAL = {
  approve: { purpose: 'agent_proposal.approve', targetType: 'agent_proposal' },
  revert: { purpose: 'agent_proposal.revert', targetType: 'agent_proposal' },
} as const;

/** O alvo de um desafio de step-up por passkey. */
export interface PasskeyStepUpTarget {
  purpose: string;
  targetType: string;
  targetId: string;
}

export function stepUpTarget(
  kind: keyof typeof STEP_UP_PURPOSES,
  targetId: string,
): PasskeyStepUpTarget {
  return { ...STEP_UP_PURPOSES[kind], targetId };
}

export interface PasskeyDto {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  revokedByAccountId: string | null;
  active: boolean;
}

export interface PasskeyList {
  accountId: string;
  activeCount: number;
  maxActive: number;
  items: PasskeyDto[];
}

interface OptionsResponse {
  challengeId: string;
  expiresAt: string;
  publicKey: Record<string, unknown>;
}

// ---- API -----------------------------------------------------------------------------------

export async function listMyPasskeys(): Promise<PasskeyList> {
  const { data } = await api.get<PasskeyList>(PASSKEYS_URL);
  return data;
}

export async function listAccountPasskeys(accountId: string): Promise<PasskeyList> {
  const { data } = await api.get<PasskeyList>(`/platform/accounts/${encodeURIComponent(accountId)}/passkeys`);
  return data;
}

export async function revokePasskey(passkeyId: string): Promise<PasskeyDto> {
  const { data } = await api.post<PasskeyDto>(`${PASSKEYS_URL}/${encodeURIComponent(passkeyId)}/revoke`, {});
  passkeyAvailability = null;
  return data;
}

/**
 * Cadastro completo: opções (com o TOTP em `X-Platform-Step-Up`, que a API exige), cerimônia do
 * navegador e conclusão. O código TOTP é o do modal de step-up; a passkey não cadastra outra.
 */
export async function registerPasskey(totpCode: string, label: PasskeyLabel): Promise<PasskeyDto> {
  const { data: options } = await api.post<OptionsResponse>(
    PASSKEY_REGISTRATION_OPTIONS_URL,
    {},
    { headers: { 'X-Platform-Step-Up': totpCode } },
  );
  const credential = await navigator.credentials.create({ publicKey: toCreationOptions(options.publicKey) });
  if (!credential) throw new PasskeyCancelledError();
  const { data } = await api.post<PasskeyDto>(PASSKEY_REGISTRATION_COMPLETE_URL, {
    challengeId: options.challengeId,
    label,
    credential: credentialToJson(credential as PublicKeyCredential),
  });
  passkeyAvailability = null;
  return data;
}

/**
 * Step-up por passkey: pede o desafio para o alvo, assina no navegador e devolve o valor do
 * cabeçalho `X-Platform-Step-Up-Passkey` (`{challengeId, credential}` em base64url).
 */
export async function passkeyStepUpHeader(target: PasskeyStepUpTarget): Promise<string> {
  const { data: options } = await api.post<OptionsResponse>(PASSKEY_STEP_UP_OPTIONS_URL, target);
  const credential = await navigator.credentials.get({ publicKey: toRequestOptions(options.publicKey) });
  if (!credential) throw new PasskeyCancelledError();
  return encodeStepUpPasskey(options.challengeId, credentialToJson(credential as PublicKeyCredential));
}

/** O operador fechou o diálogo do navegador ou o autenticador não respondeu. */
export class PasskeyCancelledError extends Error {
  constructor() {
    super('A confirmação por passkey foi cancelada. Tente de novo ou use o código do autenticador.');
    this.name = 'PasskeyCancelledError';
  }
}

/** O navegador recusou a cerimônia (cancelada, tempo esgotado, sem autenticador): mensagem da tela. */
export function describePasskeyFailure(err: unknown): string | null {
  if (err instanceof PasskeyCancelledError) return err.message;
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
      return 'A confirmação por passkey foi cancelada ou o tempo acabou. Tente de novo ou use o código do autenticador.';
    }
    if (err.name === 'InvalidStateError') return 'Esta passkey já está cadastrada neste autenticador.';
    return 'O navegador não conseguiu usar a passkey. Use o código do autenticador.';
  }
  return null;
}

// ---- disponibilidade -----------------------------------------------------------------------

/** O navegador tem WebAuthn. Sem ele (ou no jsdom dos testes), a tela só oferece o TOTP. */
export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials
  );
}

/** Cache, por carga da página, de "o operador tem passkey ativa"; zerado no cadastro e na revogação. */
let passkeyAvailability: Promise<boolean> | null = null;

/**
 * Se a tela deve oferecer a passkey no modal de step-up: navegador com WebAuthn e conta com
 * passkey ativa. Qualquer erro vale "não": o TOTP continua sendo a alternativa.
 */
export function hasActivePasskey(): Promise<boolean> {
  if (!isPasskeySupported()) return Promise.resolve(false);
  passkeyAvailability ??= listMyPasskeys()
    .then((list) => (list?.activeCount ?? 0) > 0)
    .catch(() => {
      passkeyAvailability = null;
      return false;
    });
  return passkeyAvailability;
}

/** Só para os testes: esquece o cache de disponibilidade. */
export function resetPasskeyAvailabilityForTests(): void {
  passkeyAvailability = null;
}

// ---- conversões ----------------------------------------------------------------------------

export function base64UrlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64Url(value: ArrayBuffer | ArrayBufferView): string {
  const bytes = value instanceof ArrayBuffer ? new Uint8Array(value) : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function utf8ToBase64Url(text: string): string {
  return bufferToBase64Url(new TextEncoder().encode(text));
}

/** O valor do cabeçalho `X-Platform-Step-Up-Passkey`. */
export function encodeStepUpPasskey(challengeId: string, credential: unknown): string {
  return utf8ToBase64Url(JSON.stringify({ challengeId, credential }));
}

type Descriptor = { type: string; id: string; transports?: string[] };

function descriptors(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return (list as Descriptor[]).map((d) => ({
    type: 'public-key',
    id: base64UrlToBuffer(d.id),
    ...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
  }));
}

/** `PublicKeyCredentialCreationOptions` a partir do JSON da API. */
export function toCreationOptions(json: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const user = json.user as { id: string; name: string; displayName: string };
  return {
    ...(json as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64UrlToBuffer(json.challenge as string),
    user: { ...user, id: base64UrlToBuffer(user.id) },
    excludeCredentials: descriptors(json.excludeCredentials),
  };
}

/** `PublicKeyCredentialRequestOptions` a partir do JSON da API. */
export function toRequestOptions(json: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  return {
    ...(json as unknown as PublicKeyCredentialRequestOptions),
    challenge: base64UrlToBuffer(json.challenge as string),
    allowCredentials: descriptors(json.allowCredentials),
  };
}

/** A credencial do navegador no JSON do WebAuthn (o formato que a API lê). */
export function credentialToJson(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const json: Record<string, unknown> = {
    clientDataJSON: bufferToBase64Url(response.clientDataJSON),
  };
  if ('attestationObject' in response && response.attestationObject) {
    json.attestationObject = bufferToBase64Url(response.attestationObject);
    const transports = typeof response.getTransports === 'function' ? response.getTransports() : undefined;
    if (transports) json.transports = transports;
  }
  if ('authenticatorData' in response && response.authenticatorData) {
    json.authenticatorData = bufferToBase64Url(response.authenticatorData);
    json.signature = bufferToBase64Url(response.signature);
    if (response.userHandle) json.userHandle = bufferToBase64Url(response.userHandle);
  }
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: json,
    clientExtensionResults:
      typeof credential.getClientExtensionResults === 'function' ? credential.getClientExtensionResults() : {},
  };
}
